/**
 * Shared API client for the v4 frontend.
 *
 * Session: httpOnly refresh cookie + short-lived access token (Bearer or ci_access cookie).
 * Legacy localStorage ci_token is kept in sync for ?token= PDF/download URLs.
 */

export const TOKEN_KEY = 'ci_token'
const LEGACY_TOKEN_KEYS = [TOKEN_KEY, 'caib_token'] as const

let memoryAccessToken: string | null = null
let refreshInFlight: Promise<boolean> | null = null

export function setAccessToken(token: string | null): void {
  memoryAccessToken = token
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch { /* private browsing */ }
}

/** JWT from memory, localStorage, or sessionStorage (legacy keys). */
export function getToken(): string | null {
  if (memoryAccessToken) return memoryAccessToken
  try {
    for (const key of LEGACY_TOKEN_KEYS) {
      const v = localStorage.getItem(key) || sessionStorage.getItem(key)
      if (v) {
        memoryAccessToken = v
        return v
      }
    }
    return null
  } catch {
    return null
  }
}

/**
 * Build API URL rooted at /api/* — SPA-relative bases return HTML, not JSON.
 */
export function resolveApiUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  const envBase = (import.meta.env.VITE_API_URL as string | undefined)?.trim() ?? ''

  if (envBase.startsWith('http://') || envBase.startsWith('https://')) {
    return `${envBase.replace(/\/$/, '')}${cleanPath}`
  }
  if (envBase.startsWith('/api')) {
    return `${envBase.replace(/\/$/, '')}${cleanPath}`
  }
  return cleanPath
}

/** Silent refresh via httpOnly refresh cookie — no redirect. */
export async function silentRefreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const res = await fetch(resolveApiUrl('/api/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) return false
      const data = (await res.json()) as { token?: string }
      if (data.token) setAccessToken(data.token)
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

export async function logoutSession(): Promise<void> {
  try {
    await fetch(resolveApiUrl('/api/auth/logout'), {
      method: 'POST',
      credentials: 'include',
    })
  } catch { /* best effort */ }
  setAccessToken(null)
  window.dispatchEvent(new CustomEvent('auth:logout'))
}

/**
 * Authenticated fetch with 401 → silent refresh → one retry.
 * Use for multipart FormData and other non-JSON requests (apiFetch uses rawFetch).
 */
export async function fetchWithAuthRetry(
  path: string,
  init: RequestInit = {},
  retried = false,
): Promise<Response> {
  const token = getToken()
  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(resolveApiUrl(path), {
    ...init,
    headers,
    credentials: init.credentials ?? 'include',
  })

  if (res.status === 401 && !retried && !path.includes('/api/auth/refresh')) {
    const refreshed = await silentRefreshSession()
    if (refreshed) return fetchWithAuthRetry(path, init, true)
    setAccessToken(null)
    window.dispatchEvent(new CustomEvent('auth:logout'))
  }

  return res
}

async function rawFetch(
  method: string,
  path: string,
  body?: unknown,
  retried = false,
): Promise<Response> {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (body) headers['Content-Type'] = 'application/json'

  const res = await fetch(resolveApiUrl(path), {
    method,
    headers,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  })

  if (res.status === 401 && !retried && !path.includes('/api/auth/refresh')) {
    const refreshed = await silentRefreshSession()
    if (refreshed) return rawFetch(method, path, body, true)
    setAccessToken(null)
    window.dispatchEvent(new CustomEvent('auth:logout'))
  }

  return res
}

export async function apiFetch<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await rawFetch(method, path, body)

  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string; detail?: string; message?: string }
    const msg = err.detail || err.message || err.error || `HTTP ${res.status}`
    const errorObj = new Error(msg) as Error & { status?: number }
    errorObj.status = res.status
    throw errorObj
  }

  return res.json() as Promise<T>
}
